// Base API Client Configuration and Types
import environmentService from "../config/environment.config";
import {
  getAccessToken,
  getRefreshToken,
  setAccessToken,
  setTokenExpiresAt,
} from "../utils/authStorage";
import { recordApiTiming } from "../utils/apiMetrics";

export interface ApiClientConfig {
  baseURL: string;
  timeout: number;
  retryAttempts: number;
  retryDelay: number;
}

export interface RequestConfig extends RequestInit {
  timeout?: number;
  skipAuth?: boolean;
  retryAttempts?: number;
  params?: Record<string, unknown>;
  responseType?: "json" | "blob" | "text";
  /** External abort signal (e.g. from React Query) — merged with the internal timeout signal */
  externalSignal?: AbortSignal;
}

export interface ApiError {
  message: string;
  code?: string;
  statusCode: number;
  details?: Record<string, unknown>;
}

export interface ApiResponse<T = unknown> {
  data: T;
  success: boolean;
  message?: string;
  errors?: string[];
}

// JWT Token structure
export interface AuthToken {
  access_token: string;
  token_type: string;
  expires_in: number;
  refresh_token?: string;
  scope?: string;
}

// Request/Response interceptor types
export type RequestInterceptor = (
  config: RequestConfig,
) => RequestConfig | Promise<RequestConfig>;
export type ResponseInterceptor = <T>(
  response: ApiResponse<T>,
) => ApiResponse<T> | Promise<ApiResponse<T>>;
export type ErrorInterceptor = (error: ApiError) => never | Promise<never>;

export class ApiClient {
  private config: ApiClientConfig;
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];
  private errorInterceptors: ErrorInterceptor[] = [];

  // Token refresh state — ensures only one refresh runs at a time
  private isRefreshing = false;
  private refreshQueue: Array<{
    resolve: (value: boolean) => void;
  }> = [];

  constructor(config: Partial<ApiClientConfig> = {}) {
    this.config = {
      baseURL: environmentService.apiBaseUrl,
      timeout: 30000,
      retryAttempts: 1, // No client-level retry — React Query owns the retry strategy
      retryDelay: 1000,
      ...config,
    };

    // Add default interceptors
    this.addRequestInterceptor(this.addCredentials.bind(this));
    this.addRequestInterceptor(this.addDefaultHeaders.bind(this));
    this.addErrorInterceptor(this.handleAuthError.bind(this));
  }

  // Public getter for baseURL
  get baseURL(): string {
    return this.config.baseURL;
  }

  // Interceptor management
  addRequestInterceptor(interceptor: RequestInterceptor): void {
    this.requestInterceptors.push(interceptor);
  }

  addResponseInterceptor(interceptor: ResponseInterceptor): void {
    this.responseInterceptors.push(interceptor);
  }

  addErrorInterceptor(interceptor: ErrorInterceptor): void {
    this.errorInterceptors.push(interceptor);
  }

  // Default interceptors
  private addCredentials(config: RequestConfig): RequestConfig {
    // Always send cookies (for backends that set httpOnly cookies)
    config.credentials = "include";

    // Also attach Authorization header as fallback if a token exists
    // in sessionStorage (for backends that haven't adopted cookies yet)
    const token = getAccessToken();
    if (token) {
      config.headers = {
        ...((config.headers as Record<string, string>) || {}),
        Authorization: `Bearer ${token}`,
      };
    }

    return config;
  }

  private addDefaultHeaders(config: RequestConfig): RequestConfig {
    // Don't set Content-Type for FormData - browser will set it with boundary
    const isFormData = config.body instanceof FormData;

    config.headers = {
      ...(!isFormData && { "Content-Type": "application/json" }),
      Accept: "application/json",
      ...config.headers,
    };
    return config;
  }

  private async handleAuthError(error: ApiError): Promise<never> {
    if (error.statusCode === 401) {
      // Don't dispatch auth:logout here — requestWithRetry will attempt
      // a silent token refresh first. Only if that refresh fails will
      // auth:logout be dispatched (see requestWithRetry 401 branch).
      // Dispatching here caused a race condition: AuthContext ran its own
      // concurrent refresh, and the two could invalidate each other.
    }
    throw error;
  }

  /**
   * Attempt to refresh the access token.
   * If a refresh is already in progress, queues the caller and resolves
   * when the in-flight refresh completes (prevents concurrent refresh calls).
   */
  private async attemptTokenRefresh(): Promise<boolean> {
    // If a refresh is already in-flight, queue this caller
    if (this.isRefreshing) {
      return new Promise<boolean>((resolve) => {
        this.refreshQueue.push({ resolve });
      });
    }

    this.isRefreshing = true;

    try {
      const storedRefreshToken = getRefreshToken();

      const response = await fetch(
        `${this.config.baseURL}/user-management/auth/refresh`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(storedRefreshToken
              ? { Authorization: `Bearer ${storedRefreshToken}` }
              : {}),
          },
          body: JSON.stringify(
            storedRefreshToken ? { refreshToken: storedRefreshToken } : {},
          ),
        },
      );

      if (!response.ok) {
        this.drainRefreshQueue(false);
        return false;
      }

      const responseData = await response.json();

      if (!responseData.success) {
        this.drainRefreshQueue(false);
        return false;
      }

      // Update stored tokens
      const data = responseData.data;
      if (data?.token) {
        setAccessToken(data.token);
      }
      if (data?.expiresAt) {
        setTokenExpiresAt(data.expiresAt);
      }

      // Notify AuthContext to update session state
      const event = new CustomEvent("auth:refreshed", { detail: data });
      window.dispatchEvent(event);

      this.drainRefreshQueue(true);
      return true;
    } catch {
      this.drainRefreshQueue(false);
      return false;
    } finally {
      this.isRefreshing = false;
    }
  }

  /** Resolve all queued callers waiting for the refresh result */
  private drainRefreshQueue(success: boolean): void {
    const queue = [...this.refreshQueue];
    this.refreshQueue = [];
    queue.forEach(({ resolve }) => resolve(success));
  }

  // Token management — tokens are now in httpOnly cookies (issue #24)
  // These stubs remain so error interceptors can still trigger logout
  private clearAuthToken(): void {
    // httpOnly cookies are cleared server-side on logout
    // Nothing to remove client-side
  }

  // Core request method with retry logic and silent token refresh
  private async requestWithRetry<T>(
    url: string,
    config: RequestConfig,
    attempt: number = 1,
    hasRetriedAfterRefresh: boolean = false,
  ): Promise<ApiResponse<T>> {
    const maxAttempts = config.retryAttempts ?? this.config.retryAttempts;

    try {
      const response = await this.executeRequest<T>(url, config);
      return response;
    } catch (error) {
      const apiError = error as ApiError;

      // On 401, attempt silent token refresh before giving up
      // Skip for auth endpoints to avoid infinite loops
      const isRefreshable =
        apiError.statusCode === 401 &&
        !hasRetriedAfterRefresh &&
        !config.skipAuth &&
        !url.includes("/auth/");

      if (isRefreshable) {
        const refreshed = await this.attemptTokenRefresh();
        if (refreshed) {
          return this.requestWithRetry<T>(url, config, attempt, true);
        }

        // Refresh failed — notify AuthContext to log the user out
        window.dispatchEvent(
          new CustomEvent("auth:logout", {
            detail: { reason: "token_expired" },
          }),
        );
      }

      if (attempt < maxAttempts && this.shouldRetry(apiError)) {
        await this.delay(this.config.retryDelay * attempt);
        return this.requestWithRetry<T>(
          url,
          config,
          attempt + 1,
          hasRetriedAfterRefresh,
        );
      }
      throw error;
    }
  }

  private shouldRetry(error: ApiError): boolean {
    // Retry on network errors or 5xx server errors
    return error.statusCode >= 500 || error.statusCode === 0;
  }

  private getHttpStatusMessage(status: number): string {
    const messages: Record<number, string> = {
      400: "Bad request. Please check your input and try again.",
      401: "Authentication required. Please log in again.",
      403: "You don't have permission to perform this action.",
      404: "The requested resource was not found.",
      408: "Request timed out. Please try again.",
      413: "The file is too large. Please reduce the file size and try again.",
      429: "Too many requests. Please wait a moment and try again.",
      500: "An unexpected server error occurred.",
      502: "The server is temporarily unavailable (gateway timeout). Please try again in a few moments.",
      503: "The service is currently unavailable. Please try again later.",
      504: "Server took too long to respond. Please try again later.",
    };
    return (
      messages[status] || `Server error (${status}). Please try again later.`
    );
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  // Parse the response body based on the configured response type
  private async parseResponseBody(
    response: Response,
    config: RequestConfig,
  ): Promise<unknown> {
    const responseType = config.responseType || "json";

    if (responseType === "blob") {
      return response.blob();
    }

    if (responseType === "text") {
      return response.text();
    }

    const contentType = response.headers.get("content-type");
    if (contentType?.includes("application/json")) {
      return response.json();
    }

    return response.text();
  }

  // Build an ApiError from a non-ok response and its parsed body
  private classifyAndBuildError(
    response: Response,
    responseData: unknown,
  ): ApiError {
    const errorData = responseData as Record<string, unknown>;
    // Handle nested error object: { success: false, error: { code, message } }
    const nestedError = errorData.error as Record<string, unknown> | undefined;

    // Sanitize error message — never expose raw HTML to the UI
    let errorMessage =
      (nestedError?.message as string) || (errorData.message as string);

    if (!errorMessage && typeof responseData === "string") {
      // If the response is HTML (e.g. 502 from a reverse proxy), use a clean status message
      const isHtml = responseData.trimStart().startsWith("<");
      errorMessage = isHtml
        ? this.getHttpStatusMessage(response.status)
        : responseData;
    }

    // If the message looks like HTML (e.g. Cloudflare 502 page), replace with clean message
    if (errorMessage && errorMessage.trimStart().startsWith("<")) {
      errorMessage = this.getHttpStatusMessage(response.status);
    }

    return {
      message: errorMessage || `HTTP ${response.status}`,
      statusCode: response.status,
      code: (nestedError?.code as string) || (errorData.code as string),
      details: errorData.details as Record<string, unknown>,
    };
  }

  // Execute the actual HTTP request
  private async executeRequest<T>(
    url: string,
    config: RequestConfig,
  ): Promise<ApiResponse<T>> {
    // Apply request interceptors
    let requestConfig = config;
    for (const interceptor of this.requestInterceptors) {
      requestConfig = await interceptor(requestConfig);
    }

    // Handle query parameters
    let fullUrl = `${this.config.baseURL}${url}`;
    if (requestConfig.params) {
      const searchParams = new URLSearchParams();
      Object.entries(requestConfig.params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const queryString = searchParams.toString();
      if (queryString) {
        fullUrl += (url.includes("?") ? "&" : "?") + queryString;
      }
    }

    const timeout = requestConfig.timeout ?? this.config.timeout;

    // Create abort controller for timeout — also respect external abort signal (e.g. from React Query)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    // If an external signal is provided, forward its abort to our controller
    const externalSignal = requestConfig.externalSignal;
    if (externalSignal) {
      if (externalSignal.aborted) {
        controller.abort();
      } else {
        externalSignal.addEventListener("abort", () => controller.abort(), {
          once: true,
        });
      }
    }

    try {
      const fetchStart = performance.now();
      const response = await fetch(fullUrl, {
        ...requestConfig,
        signal: controller.signal,
      });
      const fetchDuration = performance.now() - fetchStart;

      clearTimeout(timeoutId);

      // Record API timing for metrics
      recordApiTiming({
        url: url,
        method: (requestConfig.method || "GET").toUpperCase(),
        duration: fetchDuration,
        status: response.status,
        timestamp: Date.now(),
      });

      const responseData = await this.parseResponseBody(
        response,
        requestConfig,
      );

      if (!response.ok) {
        const error = this.classifyAndBuildError(response, responseData);

        // Apply error interceptors
        for (const interceptor of this.errorInterceptors) {
          await interceptor(error);
        }
      }

      // For blob responses, return the blob directly
      if ((requestConfig.responseType || "json") === "blob") {
        const apiResponse: ApiResponse<T> = {
          data: responseData as T,
          success: response.ok,
        };
        return apiResponse;
      }

      const responseAsRecord = responseData as Record<string, unknown>;
      const apiResponse: ApiResponse<T> = {
        data: (responseAsRecord.data as T) || (responseData as T),
        success: response.ok,
        message: responseAsRecord.message as string,
        errors: responseAsRecord.errors as string[],
      };

      // Apply response interceptors
      let finalResponse = apiResponse;
      for (const interceptor of this.responseInterceptors) {
        finalResponse = await interceptor(finalResponse);
      }

      return finalResponse;
    } catch (error) {
      clearTimeout(timeoutId);

      if (error instanceof Error && error.name === "AbortError") {
        const timeoutError: ApiError = {
          message: "Request timeout",
          statusCode: 408,
          code: "TIMEOUT",
        };

        for (const interceptor of this.errorInterceptors) {
          await interceptor(timeoutError);
        }
      }

      if (
        error instanceof TypeError &&
        error.message.includes("Failed to fetch")
      ) {
        const networkError: ApiError = {
          message: "Network error - please check your connection",
          statusCode: 0,
          code: "NETWORK_ERROR",
        };

        for (const interceptor of this.errorInterceptors) {
          await interceptor(networkError);
        }
      }

      throw error;
    }
  }

  // HTTP Methods
  async get<T>(
    url: string,
    config: RequestConfig = {},
  ): Promise<ApiResponse<T>> {
    return this.requestWithRetry<T>(url, { ...config, method: "GET" });
  }

  async post<T>(
    url: string,
    data?: unknown,
    config: RequestConfig = {},
  ): Promise<ApiResponse<T>> {
    return this.requestWithRetry<T>(url, {
      ...config,
      method: "POST",
      body:
        data instanceof FormData
          ? data
          : data
            ? JSON.stringify(data)
            : undefined,
    });
  }

  async put<T>(
    url: string,
    data?: unknown,
    config: RequestConfig = {},
  ): Promise<ApiResponse<T>> {
    return this.requestWithRetry<T>(url, {
      ...config,
      method: "PUT",
      body:
        data instanceof FormData
          ? data
          : data
            ? JSON.stringify(data)
            : undefined,
    });
  }

  async patch<T>(
    url: string,
    data?: unknown,
    config: RequestConfig = {},
  ): Promise<ApiResponse<T>> {
    return this.requestWithRetry<T>(url, {
      ...config,
      method: "PATCH",
      body:
        data instanceof FormData
          ? data
          : data
            ? JSON.stringify(data)
            : undefined,
    });
  }

  async delete<T>(
    url: string,
    config: RequestConfig = {},
  ): Promise<ApiResponse<T>> {
    return this.requestWithRetry<T>(url, { ...config, method: "DELETE" });
  }

  // File upload method
  async upload<T>(
    url: string,
    file: File,
    additionalData?: Record<string, unknown>,
    config: RequestConfig = {},
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append("file", file);

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(
          key,
          typeof value === "string" ? value : JSON.stringify(value),
        );
      });
    }

    // Remove Content-Type header to let browser set it with boundary
    const headers = { ...((config.headers as Record<string, string>) || {}) };
    delete headers["Content-Type"];

    return this.requestWithRetry<T>(url, {
      ...config,
      method: "POST",
      headers,
      body: formData,
    });
  }

  // Authentication methods
  async login(credentials: {
    email: string;
    password: string;
  }): Promise<ApiResponse<AuthToken>> {
    const response = await this.post<AuthToken>("/auth/login", credentials, {
      skipAuth: true,
    });

    return response;
  }

  async logout(): Promise<void> {
    try {
      await this.post("/auth/logout");
    } catch {
      // Ignore logout errors
    } finally {
      this.clearAuthToken();
    }
  }

  async refreshToken(): Promise<boolean> {
    try {
      const response = await this.post<AuthToken>(
        "/auth/refresh",
        {},
        { skipAuth: true },
      );

      if (response.success && response.data) {
        return true;
      }
    } catch {
      // Refresh failed
    }

    return false;
  }

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.get("/health", {
        skipAuth: true,
        timeout: 5000,
      });
      return response.success;
    } catch {
      return false;
    }
  }
}

// Create and export default instance
export const apiClient = new ApiClient();

// Export for dependency injection in tests
export default apiClient;
